import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

interface TimerControls {
  resetTimer: () => void;
  stopTimer: () => void;
  startTimer: () => void;
  getTimerState: () => {
    isRunning: boolean;
    time: number;
    currentCycleCount: number;
    isAlarmActive: boolean;
    cycleCount?: number;
  };
}

interface TimerContextType {
  resetAllTimers: () => void;
  resetTimer: (reminderId: string) => void;
  registerTimer: (reminderId: string, controls: TimerControls) => void;
  unregisterTimer: (reminderId: string) => void;
  deleteAllTimers: () => void;
  hideAllTimerDisplays: () => void;
  stopTimer: (reminderId: string) => void;
  dismissAllAlarms: () => void;
  areTimerDisplaysHidden: boolean;
  hasActiveAlarms: () => boolean;
  hasRunningTimers: () => boolean;
  hasRunningTimersValue: boolean;
  notifyTimerRunningStateChange: (reminderId: string, isRunning: boolean) => void;
}

const TimerContext = createContext<TimerContextType | null>(null);

interface TimerProviderProps {
  children: ReactNode;
}

export function TimerProvider({ children }: TimerProviderProps) {
  const [timers, setTimers] = useState<{ [key: string]: TimerControls }>({});
  const [areTimerDisplaysHidden, setAreTimerDisplaysHidden] = useState(false);
  const [hasRunningTimersValue, setHasRunningTimersValue] = useState(false);
  const [runningTimersCount, setRunningTimersCount] = useState(0);
  // Track previous running state for each timer to detect changes
  const timerRunningStatesRef = useRef<{ [key: string]: boolean }>({});

  // Update hasRunningTimersValue based on running timers count
  useEffect(() => {
    setHasRunningTimersValue(runningTimersCount > 0);
  }, [runningTimersCount]);

  const notifyTimerRunningStateChange = useCallback((reminderId: string, isRunning: boolean) => {
    const wasRunning = timerRunningStatesRef.current[reminderId] ?? false;

    // Update the ref
    timerRunningStatesRef.current[reminderId] = isRunning;

    // Update count only if state actually changed
    if (isRunning && !wasRunning) {
      // Timer started
      setRunningTimersCount((prev) => prev + 1);
    } else if (!isRunning && wasRunning) {
      // Timer stopped
      setRunningTimersCount((prev) => Math.max(0, prev - 1));
    }
  }, []);

  const registerTimer = useCallback((reminderId: string, controls: TimerControls) => {
    // Check initial state and update count
    const initialState = controls.getTimerState();
    timerRunningStatesRef.current[reminderId] = initialState.isRunning;
    if (initialState.isRunning) {
      setRunningTimersCount((prev) => prev + 1);
    }

    setTimers((prev) => ({ ...prev, [reminderId]: controls }));
  }, []);

  const unregisterTimer = useCallback((reminderId: string) => {
    setTimers((prev) => {
      const timer = prev[reminderId];
      // If timer was running, decrement count
      if (timer) {
        const wasRunning = timerRunningStatesRef.current[reminderId] ?? false;
        if (wasRunning) {
          setRunningTimersCount((prevCount) => Math.max(0, prevCount - 1));
        }
        delete timerRunningStatesRef.current[reminderId];
      }
      const newTimers = { ...prev };
      delete newTimers[reminderId];
      return newTimers;
    });
  }, []);

  const resetAllTimers = useCallback(() => {
    Object.values(timers).forEach((timer) => {
      timer.resetTimer();
    });
  }, [timers]);

  const resetTimer = useCallback(
    (reminderId: string) => {
      const timer = timers[reminderId];
      if (timer) {
        timer.resetTimer();
      }
    },
    [timers],
  );

  const stopTimer = useCallback(
    (reminderId: string) => {
      const timer = timers[reminderId];
      if (timer) {
        timer.stopTimer();
      }
    },
    [timers],
  );

  const deleteAllTimers = useCallback(() => {
    Object.values(timers).forEach((timer) => {
      timer.stopTimer();
    });
    // Force-clear running state immediately to prevent blockers/modals
    // from firing during transitions that intentionally stop reminders.
    timerRunningStatesRef.current = {};
    setRunningTimersCount(0);
    setHasRunningTimersValue(false);
    setTimers({});
  }, [timers]);

  const hideAllTimerDisplays = useCallback(() => {
    setAreTimerDisplaysHidden(true);
  }, []);

  const hasActiveAlarms = useCallback(() => {
    return Object.values(timers).some((timer) => {
      const state = timer.getTimerState();
      return state.isAlarmActive;
    });
  }, [timers]);

  const hasRunningTimers = useCallback(() => {
    return Object.values(timers).some((timer) => {
      const state = timer.getTimerState();
      return state.isRunning;
    });
  }, [timers]);

  const dismissAllAlarms = useCallback(() => {
    // Get all timers with active alarms first
    const timersWithActiveAlarms = Object.values(timers).filter((timer) => {
      const state = timer.getTimerState();
      return state.isAlarmActive;
    });

    // Stop all active alarm timers immediately
    timersWithActiveAlarms.forEach((timer) => {
      timer.stopTimer();
    });

    // For each timer: if this alarm completes the final reminder, just stop (don't reset).
    // At alarm time, currentCycleCount = completed cycles so far; this alarm = 1 more.
    // So we're done when currentCycleCount + 1 >= cycleCount.
    timersWithActiveAlarms.forEach((timer) => {
      const state = timer.getTimerState();
      const hasReachedLimit =
        state.cycleCount !== undefined && state.currentCycleCount + 1 >= state.cycleCount;

      if (hasReachedLimit) {
        // Already stopped above; timer is done, no reset needed
        return;
      }

      setTimeout(() => {
        timer.resetTimer();
        timer.stopTimer();
      }, 10);
    });
  }, [timers]);

  const value: TimerContextType = {
    resetAllTimers,
    resetTimer,
    registerTimer,
    unregisterTimer,
    deleteAllTimers,
    hideAllTimerDisplays,
    stopTimer,
    dismissAllAlarms,
    areTimerDisplaysHidden,
    hasActiveAlarms,
    hasRunningTimers,
    hasRunningTimersValue,
    notifyTimerRunningStateChange,
  };

  return <TimerContext.Provider value={value}>{children}</TimerContext.Provider>;
}

export function useTimerContext(): TimerContextType {
  const context = useContext(TimerContext);
  if (!context) {
    throw new Error("useTimerContext must be used within a TimerProvider");
  }
  return context;
}

export function useOptionalTimerContext(): TimerContextType | null {
  return useContext(TimerContext);
}
